'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { type ProjectOption, useProjectOptions } from '@/@reports/wizard/use-project-options';

import type { QuestionSuggestion, SurveyChoice, SurveyQuestionChoice } from '../questions';
import type { ConnectChannelKey } from '../types';

/** How many matches a questionnaire search lists. */
const MAX_MATCHES = 6;

/** The project number inside a `NNNNN-Name` reference, for finding a
 * remembered survey that is not on the first page of the list. */
function projectNumber(ref: string): string {
    return ref.match(/^\d{4,}/)?.[0] ?? '';
}

function toSurveyChoice(option: ProjectOption): SurveyChoice {
    return { ref: option.ref, name: option.projectName, detail: option.accountName || undefined };
}

/**
 * What Join my survey's questions are built from: the user's surveys (the
 * same list and search as the Reports survey picker), the picked survey's
 * questionnaire (`/api/connect/survey-questions`), and which of its questions
 * best match the channel's data.
 *
 * Every list is empty while loading and on failure; the questions then fall
 * back to "Let DANI choose" and, for a questionnaire that cannot be read, a
 * typed answer.
 */
export function useJoinSurveySources({
    enabled,
    withQuestions,
    channel,
    surveySearch,
    pickedSurvey,
    questionSearch,
}: {
    enabled: boolean;
    /** Whether the use case also asks which of the survey's questions to use
     * (so its questionnaire is worth reading). */
    withQuestions: boolean;
    channel: ConnectChannelKey;
    surveySearch: string;
    /** The picked survey's reference, or '' for none or "Let DANI list". */
    pickedSurvey: string;
    questionSearch: string;
}) {
    const list = useProjectOptions(enabled ? surveySearch : '');

    // Every project seen so far, by reference, so a pick stays resolvable
    // (and keeps its survey ids) after the search that found it changes.
    const seen = useRef(new Map<string, ProjectOption>());
    useEffect(() => {
        for (const option of list.options) seen.current.set(option.ref, option);
    }, [list.options]);

    // A remembered survey may sit beyond the first page: look it up by its
    // number. With nothing to look up this is the same query as the list
    // above, so React Query serves both from one request.
    const lookupTerm =
        enabled && pickedSurvey && !seen.current.has(pickedSurvey)
            ? projectNumber(pickedSurvey)
            : '';
    const lookup = useProjectOptions(lookupTerm || (enabled ? surveySearch : ''));
    useEffect(() => {
        for (const option of lookup.options) seen.current.set(option.ref, option);
    }, [lookup.options]);

    const picked =
        seen.current.get(pickedSurvey) ??
        list.options.find((option) => option.ref === pickedSurvey) ??
        lookup.options.find((option) => option.ref === pickedSurvey);

    const questionnaireQuery = useQuery({
        queryKey: ['connect-survey-questions', picked?.id, pickedSurvey],
        enabled: enabled && withQuestions && Boolean(picked),
        staleTime: 5 * 60_000,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: async (): Promise<SurveyQuestionChoice[]> => {
            const params = new URLSearchParams({ project: picked!.id, ref: pickedSurvey });
            const res = await fetch(`/api/connect/survey-questions?${params}`);
            if (!res.ok) return [];
            const body = (await res.json()) as { questions?: SurveyQuestionChoice[] };
            return body.questions ?? [];
        },
    });
    const questionnaire = useMemo(() => questionnaireQuery.data ?? [], [questionnaireQuery.data]);

    const suggestions = useQuery({
        queryKey: ['connect-question-suggestions', channel, pickedSurvey],
        enabled: enabled && questionnaire.length > 0,
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: async (): Promise<QuestionSuggestion[]> => {
            const res = await fetch('/api/connect/suggestions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kind: 'questions',
                    channel,
                    survey: pickedSurvey,
                    questions: questionnaire,
                }),
            });
            if (!res.ok) return [];
            const body = (await res.json()) as { questions?: QuestionSuggestion[] };
            return body.questions ?? [];
        },
    });

    const questionSuggestions = useMemo(
        () =>
            (suggestions.data ?? []).flatMap((s) => {
                const question = questionnaire.find((q) => q.qid === s.qid);
                return question ? [{ ...question, reason: s.reason }] : [];
            }),
        [suggestions.data, questionnaire],
    );

    const needle = questionSearch.trim().toLowerCase();
    const questionMatches = useMemo(
        () =>
            needle
                ? questionnaire
                      .filter(
                          (q) =>
                              q.qid.toLowerCase().includes(needle) ||
                              q.text.toLowerCase().includes(needle),
                      )
                      .slice(0, MAX_MATCHES)
                : [],
        [needle, questionnaire],
    );

    const surveys = useMemo(() => {
        const choices = list.options.map(toSurveyChoice);
        // Keep the pick listed even when the current search no longer finds it.
        if (picked && !choices.some((choice) => choice.ref === picked.ref)) {
            choices.unshift(toSurveyChoice(picked));
        }
        return choices;
    }, [list.options, picked]);

    return {
        surveys,
        surveysLoading: list.isFetching || list.isWaitingForSession,
        questionSuggestions,
        questionMatches,
        hasQuestionnaire: questionnaire.length > 0,
        questionsLoading: questionnaireQuery.isFetching || suggestions.isFetching,
    };
}
